import Game from '../Game';
import { AsyncActionStatus } from '../State/AsyncAction';
import { GameActions } from '../State/Game/GameActions';
import { GameScene, IGameState } from '../State/Game/IGameState';
import RenderSystem from '../System/Render/RenderSystem';
import { ResourceDriver } from '../Utility/Resource/ResourceManager';
import Scene from './Scene';

export enum SceneTransition {
    START = 'start',
    STARTED = 'started',
    END = 'end',
    ENDED = 'ended',
}

export default class SceneManager {
    private scenes: Map<string, Scene>;
    private activeScene: Scene;

    /**
     * Cache active scenes for update loop.
     */
    private activeScenes: Scene[];
    private loadingScene: boolean = false;

    constructor() {
        this.scenes = new Map<string, Scene>();
        this.activeScenes = [];
    }

    public addScene(sceneID: string, scene: Scene) {
        scene.index = this.scenes.size;
        this.scenes.set(sceneID, scene);
        scene.sceneManagerID = sceneID;
    }

    public async startScene(
        sceneID: string,
        sceneParams?: any,
        additive?: boolean
    ) {
        if (!this.scenes.has(sceneID)) return;

        const scene = this.scenes.get(sceneID);
        scene.params = sceneParams;

        scene.init();

        Game.resource.stop(ResourceDriver.TERTIARY);

        if (!scene.loaded) {
            await scene.preload();
            scene.loaded = true;
            this.loadingScene = false;
            // After this point a new scene may be loaded.
            Game.state.dispatch(GameActions.setSceneLoaded());
        }

        if (!additive) {
            this.activeScene = scene;
        }
        scene.active = true;
        this.activeScenes.push(scene);
        scene.start();
    }

    public stopScene(sceneID: string) {
        if (!this.scenes.has(sceneID)) return;

        const scene = this.scenes.get(sceneID);

        scene.stop();
        scene.active = false;
        const index = this.activeScenes.indexOf(scene);
        this.activeScenes.splice(index, 1);
    }

    public unloadScene(sceneID: string) {
        if (!this.scenes.has(sceneID)) return;

        const scene = this.scenes.get(sceneID);

        scene.unload();
        scene.loaded = false;
    }

    public getActiveScene(): Scene {
        return this.activeScene;
    }

    public handleStateChanged(state: IGameState) {
        if (state.activeScene.status === AsyncActionStatus.QUEUED) {
            switch (state.activeScene.result) {
                case GameScene.WORLD:
                    this.transitionTo('World', {
                        worldNumber: state.worldState.worldNumber,
                    });
                    break;
                case GameScene.LEVEL:
                    this.transitionTo('Level', {
                        levelNumber: state.levelState.levelNumber,
                        attempts: state.levelState.attempts,
                    });
                    break;
            }
            Game.state.dispatch(GameActions.setSceneLoading());
        }

        this.scenes.forEach(scene => {
            if (scene.loaded && scene.active) {
                scene.handleStateChange(state);
            }
        });
    }

    public update() {
        this.activeScenes.forEach(scene => scene.update());
        RenderSystem.renderUpdate();
    }

    private async transitionTo(sceneID: string, sceneParams: any) {
        console.log('ACTIVE', this.activeScene);
        // if(this.activeScene)
        this.stopScene(this.activeScene.sceneManagerID);
        await this.startScene('Transition', {}, true);
        const transitionScene = this.scenes.get('Transition');
        transitionScene.once(SceneTransition.STARTED, () => {
            this.unloadScene(this.activeScene.sceneManagerID);
            this.startScene(sceneID, sceneParams).then(() => {
                transitionScene.once(SceneTransition.ENDED, () => {
                    Game.ticker.setImmediate(() => {
                        this.stopScene('Transition');
                        this.unloadScene('Transition');
                    });
                });
                transitionScene.emit(SceneTransition.END);
            });
        });
        transitionScene.emit(SceneTransition.START);
    }
}
